import React, { useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { BlogEnglishNotice } from '@/components/BlogEnglishNotice';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, User, Calculator, TrendingUp, DollarSign, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BlogVoiceROI = () => {
  const [teamSize, setTeamSize] = useState('10');
  const [avgSalary, setAvgSalary] = useState('80000');
  const [timeSpentOnCRM, setTimeSpentOnCRM] = useState('2');

  const calculateROI = () => {
    const size = parseInt(teamSize) || 0;
    const salary = parseInt(avgSalary) || 0;
    const hoursPerDay = parseFloat(timeSpentOnCRM) || 0;
    
    const hourlyRate = salary / (52 * 40); // Weekly hours * weeks per year
    const dailyCost = size * hoursPerDay * hourlyRate;
    const annualCRMCost = dailyCost * 250; // 250 working days
    
    const timeSavings = 0.67; // 67% time reduction
    const annualSavings = annualCRMCost * timeSavings;
    const productivityGain = annualSavings * 0.3; // Additional productivity from better data
    const totalBenefit = annualSavings + productivityGain;
    
    return {
      annualCRMCost: Math.round(annualCRMCost),
      annualSavings: Math.round(annualSavings),
      productivityGain: Math.round(productivityGain),
      totalBenefit: Math.round(totalBenefit),
      monthlyBenefit: Math.round(totalBenefit / 12)
    };
  };

  const roi = calculateROI();

  const scrollToDemo = () => {
    const currentPath = window.location.pathname;
    const isFrenchPage = currentPath.startsWith('/fr');
    
    // If we're on the home page, scroll to the form
    if (currentPath === '/' || currentPath === '/fr') {
      const element = document.getElementById('cta');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page with language prefix
      const homeUrl = isFrenchPage ? '/fr?scrollTo=cta' : '/?scrollTo=cta';
      window.location.href = homeUrl;
    }
  };

  return (
    <Layout>
      <SEOHead 
        title="Voice AI for Salesforce ROI: Save $127K/Year (Interactive Calculator)"
        description="Real ROI data: Sales teams using voice AI save 67% on CRM admin time, gain 30% productivity. Interactive calculator shows your savings. Free 30-day trial."
        keywords="voice assistant ROI calculator, sales productivity tools, CRM automation benefits, voice technology for sales, AI voice assistant cost savings"
        canonicalUrl="https://noq5.ai/blog/voice-assistant-technology-sales-teams-roi-calculator"
      />
      
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": "Voice assistant technology for sales teams: ROI calculator & implementation guide",
            "description": "Calculate the ROI of voice assistant technology for your sales team. Discover implementation strategies, cost savings, and productivity gains from AI-powered CRM automation.",
            "image": "https://noq5.ai/images/voice-roi-calculator.png",
            "author": {
              "@type": "Organization",
              "name": "NoQ5.ai Team"
            },
            "publisher": {
              "@type": "Organization",
              "name": "NoQ5.ai",
              "logo": {
                "@type": "ImageObject",
                "url": "https://noq5.ai/images/noq5-logo.png"
              }
            },
            "datePublished": "2025-01-15",
            "dateModified": "2025-01-15",
            "mainEntityOfPage": "https://noq5.ai/blog/voice-assistant-technology-sales-teams-roi-calculator"
          })
        }}
      />
      
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://noq5.ai/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://noq5.ai/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Voice assistant technology for sales teams: ROI calculator & implementation guide",
                "item": "https://noq5.ai/blog/voice-assistant-technology-sales-teams-roi-calculator"
              }
            ]
          })
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <Link 
          to="/blog" 
          className="inline-flex items-center text-primary hover:text-primary/80 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>

        <article className="prose prose-lg max-w-none">
          <header className="mb-8">
            <h1 className="text-4xl font-bold mb-4 text-foreground">
              Voice assistant technology for sales teams: ROI calculator & implementation guide
            </h1>
            <div className="flex items-center gap-4 text-muted-foreground mb-6">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Marketing Team</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>15 min read</span>
              </div>
            </div>
          </header>

          <div className="bg-oli-blue-light/10 border-l-4 border-oli-blue p-6 mb-8">
            <p className="text-sm font-semibold text-oli-blue mb-2">TL;DR — Key Takeaways</p>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li>Sales reps spend 20-50% of their day on administrative tasks, costing $50,000+ per rep annually in lost productivity</li>
              <li>Voice assistant technology delivers 67% reduction in CRM update time with 89% improved data completeness</li>
              <li>Implementation follows 4-phase roadmap: Assessment (Week 1-2), Pilot (Week 3-6), Rollout (Week 7-12), Optimization (ongoing)</li>
              <li>Key ROI drivers: time savings, productivity gains from better data quality, and increased deal close rates (+23%)</li>
              <li>Success requires measuring efficiency metrics, quality improvements, revenue impact, and user satisfaction scores</li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-primary" />
              Interactive ROI Calculator
            </h3>
            <p className="text-muted-foreground mb-6">
              Calculate the potential return on investment for implementing voice assistant technology in your sales team. Adjust the parameters below to see personalized results.
            </p>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label htmlFor="teamSize" className="text-sm font-medium">Sales Team Size</Label>
                <Input
                  id="teamSize"
                  type="number"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="avgSalary" className="text-sm font-medium">Average Salary ($)</Label>
                <Input
                  id="avgSalary"
                  type="number"
                  value={avgSalary}
                  onChange={(e) => setAvgSalary(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="timeSpentOnCRM" className="text-sm font-medium">Hours/Day on CRM</Label>
                <Input
                  id="timeSpentOnCRM"
                  type="number"
                  step="0.5"
                  value={timeSpentOnCRM}
                  onChange={(e) => setTimeSpentOnCRM(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Annual CRM Cost</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">${roi.annualCRMCost.toLocaleString()}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Time Savings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">${roi.annualSavings.toLocaleString()}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Productivity Gain</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">${roi.productivityGain.toLocaleString()}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Annual Benefit</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-primary">${roi.totalBenefit.toLocaleString()}</div>
                </CardContent>
              </Card>
            </div>
          </div>

          <section className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground">The hidden cost of CRM data entry</h2>
            
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-red-800 mb-4">
                What your sales team actually costs you
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-red-800 mb-3">Time allocation reality:</h4>
                  <ul className="text-red-700 space-y-2">
                    <li>• 20-50% of day spent on administrative tasks</li>
                    <li>• 2.5 hours daily on CRM updates and data entry</li>
                    <li>• 65% of this time could be automated</li>
                    <li>• Only 35% of work day spent actually selling</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-red-800 mb-3">Financial impact:</h4>
                  <ul className="text-red-700 space-y-2">
                    <li>• $50,000+ per rep annually in lost productivity</li>
                    <li>• 67% reduction possible with voice automation</li>
                    <li>• 23% increase in deal close rates</li>
                    <li>• $150,000+ additional revenue per rep</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground">Voice technology implementation roadmap</h2>
            
            <div className="space-y-6">
              <div className="bg-card border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">1</div>
                  <h3 className="text-xl font-semibold">Connect your CRM (30 seconds)</h3>
                </div>
                <div className="ml-11 space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Provide your CRM token</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Authenticate with OAuth</span>
                  </div>
                </div>
              </div>

              <div className="bg-card border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">2</div>
                  <h3 className="text-xl font-semibold">Start using voice (Immediately)</h3>
                </div>
                <div className="ml-11 space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">You're live - start updating your CRM with voice</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">No training, configuration, or setup required</span>
                  </div>
                </div>
              </div>

              <div className="bg-card border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">3</div>
                  <h3 className="text-xl font-semibold">See results (Day 1)</h3>
                </div>
                <div className="ml-11 space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Analyze performance data and ROI metrics</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Expand to additional workflows and use cases</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Integrate with additional sales tools</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Scale successful patterns across organization</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground">Success metrics and KPIs</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-card border rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Efficiency metrics
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Time spent on CRM updates</span>
                    <span className="font-semibold text-green-600">-67%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Data entry accuracy</span>
                    <span className="font-semibold text-green-600">+89%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Data completeness</span>
                    <span className="font-semibold text-green-600">+106%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Follow-up task completion</span>
                    <span className="font-semibold text-green-600">+45%</span>
                  </div>
                </div>
              </div>

              <div className="bg-card border rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-blue-500" />
                  Revenue metrics
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Deal close rate</span>
                    <span className="font-semibold text-blue-600">+23%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Sales cycle length</span>
                    <span className="font-semibold text-blue-600">-18%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Pipeline accuracy</span>
                    <span className="font-semibold text-blue-600">+31%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Revenue per rep</span>
                    <span className="font-semibold text-blue-600">+28%</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground">Common implementation challenges and solutions</h2>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-amber-800 mb-3">Challenge: User adoption resistance</h3>
                <p className="text-amber-700 mb-3">
                  Some team members may be hesitant to adopt new technology, especially if they're comfortable with existing workflows.
                </p>
                <div className="bg-white/50 rounded p-4">
                  <h4 className="font-semibold text-amber-800 mb-2">Solution:</h4>
                  <ul className="text-amber-700 text-sm space-y-1">
                    <li>• Start with enthusiastic early adopters</li>
                    <li>• Demonstrate clear time savings with concrete examples</li>
                    <li>• Provide comprehensive training and ongoing support</li>
                    <li>• Celebrate wins and share success stories</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-red-800 mb-3">Challenge: Data quality concerns</h3>
                <p className="text-red-700 mb-3">
                  Teams worry that voice input might reduce data accuracy compared to manual entry.
                </p>
                <div className="bg-white/50 rounded p-4">
                  <h4 className="font-semibold text-red-800 mb-2">Solution:</h4>
                  <ul className="text-red-700 text-sm space-y-1">
                    <li>• Implement validation workflows and review processes</li>
                    <li>• Show data quality improvements from consistent formatting</li>
                    <li>• Use AI-powered data enrichment and correction</li>
                    <li>• Provide real-time feedback and error correction</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">Challenge: Integration complexity</h3>
                <p className="text-blue-700 mb-3">
                  Connecting voice technology with existing CRM and sales tools can seem technically challenging.
                </p>
                <div className="bg-white/50 rounded p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Solution:</h4>
                  <ul className="text-blue-700 text-sm space-y-1">
                    <li>• Choose solutions with pre-built CRM integrations</li>
                    <li>• Work with vendors that provide technical support</li>
                    <li>• Start with basic integrations and expand gradually</li>
                    <li>• Leverage existing APIs and webhooks</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground">Real-world case study</h2>
            
            <div className="bg-gradient-to-r from-oli-blue-light/20 to-white p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-3 text-oli-blue">Dinevel Solutions</h3>
              <div className="text-sm text-gray-600 mb-3">SMB sales team • B2B SaaS</div>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-sm">Data completeness improvement:</span>
                  <span className="font-semibold text-oli-blue">43% → 89%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Time savings per rep:</span>
                  <span className="font-semibold text-oli-blue">67% reduction in CRM updates</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Implementation:</span>
                  <span className="font-semibold text-oli-blue">Quick - OAuth token setup</span>
                </div>
              </div>
              <blockquote className="text-oli-blue italic text-sm border-l-4 border-oli-blue pl-4">
                "For the first time in my career, I actually look forward to updating my CRM. It feels like I have a personal assistant who just gets it." — Jonathan F, Senior Strategic Account Executive - Global Accounts
              </blockquote>
            </div>
          </section>

          <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to save 5+ hours a week?</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Try NoQ5.ai, the Salesforce AI Copilot built for reps. Transform how your team updates CRM automatically.
            </p>
            <Button 
              onClick={scrollToDemo}
              size="lg" 
              className="font-semibold"
            >
              Try NoQ5 now →
            </Button>
          </div>

          <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Calculate your personalized ROI</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Based on your team size of {teamSize} people, you could save approximately <strong>${roi.monthlyBenefit.toLocaleString()}/month</strong> with voice assistant technology. Book a demo to see detailed projections for your specific use case.
            </p>
            <Button 
              onClick={scrollToDemo}
              size="lg" 
              className="font-semibold"
            >
              Get Custom ROI Analysis
            </Button>
          </div>
        </article>
      </motion.div>
    </Layout>
  );
};

export default BlogVoiceROI;